import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import ViewQuiltRoundedIcon from "@mui/icons-material/ViewQuiltRounded";
import EditableImage from "./EditableImage";
import {
  SEGMENT_ORDER,
  buildSegmentVisibilityChanges,
  getSegmentDefinition,
} from "../landingStudio/segmentRegistry";

const TAB_DEFINITIONS = Object.freeze({
  0: { label: "Diseño general", sectionLabel: "Diseño general" },
  1: { label: "Imágenes", sectionLabel: "Imágenes" },
  2: { label: "Secciones", sectionLabel: "Secciones de la página" },
  3: { label: "Datos del negocio", sectionLabel: "Datos del negocio" },
  4: { label: "SEO y publicación", sectionLabel: "SEO y publicación" },
});

function tabsForMode(mode) {
  if (mode === "sections") return [2];
  if (mode === "identity") return [3];
  if (mode === "legacy") return [0, 1, 2, 3, 4];
  return [0, 4];
}

function dialogTitle(mode) {
  if (mode === "sections") return "Secciones de la página";
  if (mode === "identity") return "Datos del negocio";
  if (mode === "legacy") return "Configuración de la página";
  return "Preferencias del sitio";
}

function dialogDescription(mode) {
  if (mode === "sections") return "Agregá, mostrá u ocultá bloques sin mezclar esta tarea con la configuración general.";
  if (mode === "identity") return "Estos datos se reutilizan donde corresponde en contacto, pie, buscadores y otras vistas públicas.";
  if (mode === "legacy") return "Ajustes generales y contenido de la página.";
  return "Ajustes globales o avanzados que afectan al sitio completo. El contenido de cada sección se edita desde su inspector.";
}

function clone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function normalizePublicStatus(value) {
  const candidate = String(value || "").trim().toLowerCase();
  return ["draft", "published", "maintenance"].includes(candidate)
    ? candidate
    : "draft";
}

function publicStatusLabel(value) {
  const status = normalizePublicStatus(value);
  if (status === "published") return "Publicado";
  if (status === "maintenance") return "Mantenimiento";
  return "Borrador";
}

function withoutPublicStatus(value) {
  const next = clone(value || {});
  delete next.publicStatus;
  return next;
}

function statusConfirmationMessage(status) {
  if (status === "published") {
    return "Se guardará el borrador pendiente y se publicará su última revisión real. La web pública pasará a mostrar esa versión.";
  }
  if (status === "maintenance") {
    return "La web pública mostrará la página de mantenimiento usando la última versión publicada. El borrador y el historial no se perderán.";
  }
  return "La web pública quedará oculta como borrador. La última versión publicada y todo el historial se conservarán.";
}

function getAtPath(source, path, fallback = "") {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => value?.[/^\d+$/.test(key) ? Number(key) : key], source) ?? fallback;
}

function setAtPath(source, path, value) {
  const next = clone(source);
  const keys = String(path || "").split(".").filter(Boolean);
  let cursor = next;
  keys.forEach((rawKey, index) => {
    const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    const nextKey = /^\d+$/.test(keys[index + 1]) ? Number(keys[index + 1]) : keys[index + 1];
    if (cursor[key] === undefined || cursor[key] === null) cursor[key] = typeof nextKey === "number" ? [] : {};
    cursor = cursor[key];
  });
  return next;
}

function Field({ form, path, label, onChange, multiline = false, minRows = 1, type = "text", helperText = "" }) {
  return (
    <TextField
      fullWidth
      type={type}
      label={label}
      value={getAtPath(form, path, "")}
      onChange={(event) => onChange(path, event.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      helperText={helperText}
    />
  );
}

function ColorField({ form, path, label, onChange }) {
  const value = getAtPath(form, path, "#000000") || "#000000";
  return (
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Box component="input" type="color" value={value} onChange={(event) => onChange(path, event.target.value)} sx={{ width: 48, height: 44, p: 0, border: 0, bgcolor: "transparent", cursor: "pointer" }} />
      <TextField fullWidth label={label} value={value} onChange={(event) => onChange(path, event.target.value)} />
    </Stack>
  );
}

const SECTION_GROUPS = Object.freeze([
  {
    id: "structure",
    title: "Estructura",
    description: "Inicio y navegación.",
  },
  {
    id: "commerce",
    title: "Productos y servicios",
    description: "Bloques comerciales del mismo catálogo.",
  },
  {
    id: "content",
    title: "Contenido",
    description: "Historia, beneficios y contacto.",
  },
  {
    id: "closing",
    title: "Cierre y accesos",
    description: "Final de página y acciones flotantes.",
  },
]);

function PreviewLine({ width, active, height = 6, opacity = 1 }) {
  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: 99,
        bgcolor: active ? "#F35A3A" : "rgba(25,36,47,.34)",
        opacity,
      }}
    />
  );
}

function PreviewCard({ active, height = 42, children }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height,
        borderRadius: 1.5,
        bgcolor: active ? "rgba(243,90,58,.10)" : "rgba(25,36,47,.05)",
        border: "1px solid",
        borderColor: active ? "rgba(243,90,58,.20)" : "rgba(25,36,47,.09)",
        display: "grid",
        placeItems: "center",
      }}
    >
      {children}
    </Box>
  );
}

function SegmentMiniPreview({ type = "cards", active = false }) {
  const accent = active ? "#F35A3A" : "rgba(25,36,47,.38)";
  const soft = active ? "rgba(243,90,58,.10)" : "rgba(25,36,47,.05)";

  if (type === "navigation") {
    return (
      <Stack spacing={1.3} sx={{ width: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <PreviewLine width="27%" active={active} height={9} />
          <Stack direction="row" spacing={0.65}>
            {[0, 1, 2].map((item) => <PreviewLine key={item} width={16} active={active} height={4} opacity={0.68} />)}
          </Stack>
        </Stack>
        <Box sx={{ height: 52, borderRadius: 1.8, bgcolor: soft, border: "1px solid rgba(25,36,47,.06)" }} />
      </Stack>
    );
  }

  if (type === "hero") {
    return (
      <Stack direction="row" spacing={1.4} sx={{ width: "100%", alignItems: "center" }}>
        <Stack spacing={0.85} sx={{ flex: 1.1 }}>
          <PreviewLine width="64%" active={active} height={10} />
          <PreviewLine width="90%" active={active} opacity={0.48} />
          <PreviewLine width="74%" active={active} opacity={0.34} />
          <Box sx={{ width: 38, height: 13, borderRadius: 99, bgcolor: accent, mt: 0.45 }} />
        </Stack>
        <Box sx={{ flex: 0.8, height: 72, borderRadius: 2, bgcolor: soft, border: "1px solid rgba(25,36,47,.07)" }} />
      </Stack>
    );
  }

  if (type === "split" || type === "contact") {
    return (
      <Stack direction="row" spacing={1.2} sx={{ width: "100%", alignItems: "stretch" }}>
        <PreviewCard active={active} height={72} />
        <Stack spacing={0.85} sx={{ flex: 1.15, justifyContent: "center" }}>
          <PreviewLine width="58%" active={active} height={9} />
          <PreviewLine width="94%" active={active} opacity={0.5} />
          <PreviewLine width="80%" active={active} opacity={0.36} />
          {type === "contact" ? <PreviewLine width="46%" active={active} height={10} opacity={0.78} /> : null}
        </Stack>
      </Stack>
    );
  }

  if (type === "footer") {
    return (
      <Stack spacing={1.2} sx={{ width: "100%", justifyContent: "flex-end" }}>
        <Stack direction="row" spacing={1}>
          {[0, 1, 2].map((item) => (
            <Stack key={item} spacing={0.65} sx={{ flex: 1 }}>
              <PreviewLine width={item === 0 ? "70%" : "52%"} active={active} height={6} opacity={0.72} />
              <PreviewLine width="90%" active={active} height={4} opacity={0.34} />
              <PreviewLine width="72%" active={active} height={4} opacity={0.26} />
            </Stack>
          ))}
        </Stack>
        <Box sx={{ height: 18, borderRadius: 1.2, bgcolor: soft }} />
      </Stack>
    );
  }

  if (type === "floating") {
    return (
      <Box sx={{ width: "100%", height: 82, position: "relative", borderRadius: 2, bgcolor: soft, border: "1px solid rgba(25,36,47,.06)" }}>
        <Stack spacing={0.7} sx={{ position: "absolute", left: 12, top: 15, width: "60%" }}>
          <PreviewLine width="72%" active={active} opacity={0.28} />
          <PreviewLine width="92%" active={active} opacity={0.2} />
        </Stack>
        <Box sx={{ position: "absolute", right: 10, bottom: 10, width: 30, height: 30, borderRadius: "50%", bgcolor: accent, boxShadow: "0 4px 12px rgba(25,36,47,.14)" }} />
      </Box>
    );
  }

  if (type === "promo") {
    return (
      <Stack spacing={1.1} sx={{ width: "100%" }}>
        <Box sx={{ height: 34, borderRadius: 1.8, bgcolor: accent, opacity: active ? 0.84 : 0.48 }} />
        <Stack direction="row" spacing={0.9}>
          {[0, 1, 2].map((item) => <PreviewCard key={item} active={active} height={35} />)}
        </Stack>
      </Stack>
    );
  }

  if (type === "logos") {
    return (
      <Stack spacing={1.25} sx={{ width: "100%" }}>
        <PreviewLine width="42%" active={active} height={8} opacity={0.62} />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
          {[0, 1, 2, 3].map((item) => (
            <Box key={item} sx={{ width: 31, height: 31, borderRadius: item % 2 ? 1.4 : "50%", bgcolor: item % 2 ? soft : accent, opacity: item % 2 ? 1 : 0.48 }} />
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.1} sx={{ width: "100%" }}>
      <PreviewLine width={type === "benefits" ? "46%" : "58%"} active={active} height={9} opacity={0.68} />
      <Stack direction="row" spacing={0.9}>
        {[0, 1, 2].map((item) => (
          <PreviewCard key={item} active={active} height={type === "categories" ? 48 : 58}>
            {type === "benefits" ? <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: accent, opacity: 0.62 }} /> : null}
          </PreviewCard>
        ))}
      </Stack>
    </Stack>
  );
}

function SegmentChoiceCard({ definition, active, disabled, onToggle }) {
  const presentation = definition.presentation || {};
  return (
    <Box
      sx={{
        minWidth: 0,
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: active ? "rgba(243,90,58,.40)" : "rgba(25,36,47,.11)",
        bgcolor: active ? "rgba(243,90,58,.035)" : "#fff",
        boxShadow: active ? "0 10px 26px rgba(243,90,58,.08)" : "0 6px 18px rgba(25,36,47,.045)",
        opacity: disabled ? 0.6 : 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "border-color .16s ease, box-shadow .16s ease, transform .16s ease",
        "&:hover": disabled ? undefined : {
          borderColor: active ? "rgba(243,90,58,.50)" : "rgba(25,36,47,.20)",
          boxShadow: "0 12px 28px rgba(25,36,47,.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          minHeight: 126,
          px: 2,
          py: 1.7,
          bgcolor: active ? "rgba(243,90,58,.045)" : "#F7F8FA",
          borderBottom: "1px solid rgba(25,36,47,.07)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <SegmentMiniPreview type={presentation.preview} active={active} />
      </Box>

      <Stack spacing={1.15} sx={{ p: 1.8, flex: 1 }}>
        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 950, color: "#19242F", fontSize: "1rem" }}>{definition.label}</Typography>
          <Chip
            size="small"
            icon={active ? <CheckRoundedIcon /> : undefined}
            label={active ? "En la página" : "Disponible"}
            variant={active ? "filled" : "outlined"}
            color={active ? "primary" : "default"}
            sx={{ height: 24, fontWeight: 850 }}
          />
        </Stack>

        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.74)", lineHeight: 1.45 }}>
          {presentation.summary}
        </Typography>

        <Stack spacing={0.7} sx={{ mt: "auto !important" }}>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <PlaceRoundedIcon sx={{ fontSize: 16, color: "rgba(25,36,47,.48)" }} />
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", lineHeight: 1.3 }}>
              {presentation.placement}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <ViewQuiltRoundedIcon sx={{ fontSize: 16, color: "rgba(25,36,47,.48)" }} />
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", lineHeight: 1.3 }}>
              {presentation.visualHint}
            </Typography>
          </Stack>
        </Stack>

        <Button
          fullWidth
          size="small"
          variant={active ? "outlined" : "contained"}
          startIcon={active ? <RemoveRoundedIcon /> : <AddRoundedIcon />}
          disabled={disabled}
          onClick={() => onToggle(!active)}
          sx={{ mt: 0.35, minHeight: 44, textTransform: "none", fontWeight: 900, borderRadius: 2 }}
        >
          {active ? "Quitar de la página" : "Agregar a la página"}
        </Button>
      </Stack>
    </Box>
  );
}

export default function LandingSettingsDialog({
  open,
  form,
  productsEnabled = true,
  saving = false,
  changingPublicStatus = false,
  initialTab = 0,
  requestedPublicStatus = "",
  mode = "legacy",
  showTrialGuide = false,
  onClose,
  onSave,
  onChangePublicStatus,
  onUploadAsset,
  onDeleteAsset,
}) {
  const compactViewport = useMediaQuery("(max-width:700px)");
  const touchViewport = useMediaQuery("(max-width:1100px)");
  const allowedTabs = React.useMemo(() => tabsForMode(mode), [mode]);
  const initialTabValue = allowedTabs.includes(Number(initialTab)) ? Number(initialTab) : allowedTabs[0];
  const [draft, setDraft] = React.useState(() => clone(form || {}));
  const [tab, setTab] = React.useState(initialTabValue);
  const [error, setError] = React.useState("");
  const [pendingPublicStatus, setPendingPublicStatus] = React.useState("");

  React.useEffect(() => {
    if (open) {
      const nextAllowedTabs = tabsForMode(mode);
      const requestedTab = Number(initialTab);
      const currentStatus = normalizePublicStatus(form?.publicStatus);
      const requestedStatus = requestedPublicStatus
        ? normalizePublicStatus(requestedPublicStatus)
        : "";
      setDraft({
        ...clone(form || {}),
        ...(requestedStatus ? { publicStatus: requestedStatus } : {}),
      });
      setTab(nextAllowedTabs.includes(requestedTab) ? requestedTab : nextAllowedTabs[0]);
      setError("");
      setPendingPublicStatus(requestedStatus && requestedStatus !== currentStatus ? requestedStatus : "");
    }
  }, [form, initialTab, mode, open, requestedPublicStatus]);

  const change = (path, value) => setDraft((current) => setAtPath(current, path, value));
  const changeMany = React.useCallback((changes) => {
    setDraft((current) =>
      Object.entries(changes || {}).reduce(
        (next, [path, value]) => setAtPath(next, path, value),
        current
      )
    );
  }, []);

  const segmentDefinitions = React.useMemo(
    () => SEGMENT_ORDER.map((segmentType) => getSegmentDefinition(segmentType)).filter(Boolean),
    []
  );

  const upload = async (path, file, metadata, options = {}) => {
    const asset = await onUploadAsset?.(file, metadata, options);
    const value = asset?.value || asset?.publicPath || asset?.url || "";
    if (!value) throw new Error("No se recibió la ruta del archivo subido");
    change(path, value);
  };

  const remove = async (path) => {
    const current = getAtPath(draft, path, "");
    if (current) await onDeleteAsset?.(current);
    change(path, "");
  };

  const currentPublicStatus = normalizePublicStatus(form?.publicStatus);
  const selectedPublicStatus = normalizePublicStatus(draft?.publicStatus);
  const publicStatusChanged = selectedPublicStatus !== currentPublicStatus;
  const contentChanged = React.useMemo(
    () =>
      JSON.stringify(withoutPublicStatus(draft)) !==
      JSON.stringify(withoutPublicStatus(form)),
    [draft, form]
  );
  const busy = saving || changingPublicStatus;

  const persist = async (nextPublicStatus = "") => {
    try {
      setError("");

      if (contentChanged) {
        await onSave?.(
          { ...draft, publicStatus: currentPublicStatus },
          {
            editorKey: `settings:${tab}`,
            editorType: "settings",
            tabIndex: tab,
            sectionLabel: TAB_DEFINITIONS[tab]?.sectionLabel || "Preferencias del sitio",
          }
        );
      }

      if (nextPublicStatus) {
        const result = await onChangePublicStatus?.(nextPublicStatus);
        if (!result) {
          throw new Error("No se pudo confirmar el cambio de estado público.");
        }
      }

      setPendingPublicStatus("");
      onClose?.();
    } catch (err) {
      setError(err?.message || "No se pudieron guardar los ajustes");
    }
  };

  const save = async () => {
    if (publicStatusChanged) {
      setPendingPublicStatus(selectedPublicStatus);
      return;
    }
    await persist();
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      fullScreen={compactViewport}
      maxWidth="md"
      sx={touchViewport ? {
        "& .MuiButton-root": { minHeight: 44 },
        "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
        "& .MuiInputBase-root": { minHeight: 44 },
        "& .MuiTab-root": { minHeight: 48 },
        "& input[type='color']": { minWidth: 48, minHeight: 44 },
      } : undefined}
    >
      <DialogTitle sx={{ pb: 0.5, fontWeight: 950, color: "#19242F" }}>{dialogTitle(mode)}</DialogTitle>
      <Typography variant="body2" sx={{ px: { xs: 2, sm: 3 }, pb: allowedTabs.length > 1 ? 0.5 : 1.5, color: "rgba(25,36,47,.62)" }}>
        {dialogDescription(mode)}
      </Typography>
      {allowedTabs.length > 1 ? (
        <Box sx={{ px: { xs: 0.75, sm: 2.5 }, pt: 0.5 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
            {allowedTabs.map((value) => (
              <Tab key={value} value={value} label={TAB_DEFINITIONS[value]?.label || "Ajustes"} sx={{ textTransform: "none", fontWeight: 850 }} />
            ))}
          </Tabs>
        </Box>
      ) : null}
      <DialogContent dividers>
        <Stack spacing={2.2}>
          {showTrialGuide && mode === "global" ? (
            <Alert severity="info">Las preferencias generales pueden afinarse después. Para textos, imágenes y visibilidad de bloques, editá directamente cada sección.</Alert>
          ) : showTrialGuide && mode === "legacy" ? (
            <Alert severity="info">Completá primero identidad, portada, contacto y publicación. Las imágenes avanzadas pueden agregarse después.</Alert>
          ) : null}

          {tab === 0 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 950 }}>Paleta general</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.primaryColor" label="Color principal" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.secondaryColor" label="Color oscuro" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.surfaceColor" label="Superficie" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.backgroundColor" label="Fondo general" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.textColor" label="Texto principal" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.mutedTextColor" label="Texto secundario" onChange={change} /></Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.headingFontFamily" label="Tipografía de títulos" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.bodyFontFamily" label="Tipografía de textos" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.googleFontsUrl" label="URL de Google Fonts" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.borderRadius" label="Redondeo general" type="number" onChange={(path, value) => change(path, Number(value) || 0)} /></Grid>
              </Grid>
            </Stack>
          ) : null}

          {tab === 1 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.logoUrl", "")}
                  label="Logo principal"
                  helperText="SVG, PNG, WebP o JPG."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.logoUrl", file, { sectionKey: "branding", assetKey: "logo" }, options)}
                  onRemove={() => remove("theme.logoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.compactLogoUrl", "")}
                  label="Logo compacto"
                  helperText="Versión cuadrada o reducida para celular."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.compactLogoUrl", file, { sectionKey: "branding", assetKey: "compact-logo" }, options)}
                  onRemove={() => remove("theme.compactLogoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.faviconUrl", "")}
                  label="Favicon"
                  helperText="Ícono cuadrado para la pestaña."
                  disabled={saving}
                  accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                  onUpload={(file, options) => upload("theme.faviconUrl", file, { sectionKey: "branding", assetKey: "favicon" }, options)}
                  onRemove={() => remove("theme.faviconUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "hero.imageUrl", "")}
                  label="Imagen principal de portada"
                  helperText="Se usa cuando la portada no tiene slider."
                  disabled={saving}
                  onUpload={(file, options) => upload("hero.imageUrl", file, { sectionKey: "hero", assetKey: "background" }, options)}
                  onRemove={() => remove("hero.imageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "about.imageUrl", "")}
                  label="Imagen sobre el negocio"
                  disabled={saving}
                  onUpload={(file, options) => upload("about.imageUrl", file, { sectionKey: "about", assetKey: "main" }, options)}
                  onRemove={() => remove("about.imageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "seo.imageUrl", "")}
                  label="Imagen para compartir"
                  helperText="Vista previa para redes sociales y mensajería."
                  disabled={saving}
                  onUpload={(file, options) => upload("seo.imageUrl", file, { sectionKey: "seo", assetKey: "social" }, options)}
                  onRemove={() => remove("seo.imageUrl")}
                />
              </Grid>
            </Grid>
          ) : null}

          {tab === 2 ? (
            <Stack spacing={2.4}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F" }}>
                  Elegí un bloque mirando cómo se va a sentir
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.45, color: "rgba(25,36,47,.62)" }}>
                  La miniatura muestra su forma general; el texto resume para qué sirve y dónde suele ubicarse. Todo sale del registro real del WebBuilder.
                </Typography>
              </Box>

              {!productsEnabled ? (
                <Alert severity="info">
                  Los bloques de productos y servicios aparecen como referencia, pero sólo se pueden agregar cuando ese módulo está habilitado en el proyecto.
                </Alert>
              ) : null}

              {SECTION_GROUPS.map((group) => {
                const definitions = segmentDefinitions.filter(
                  (definition) => definition.presentation?.group === group.id
                );
                if (!definitions.length) return null;
                const commercialGroup = group.id === "commerce";
                const commercialEnabled = getAtPath(draft, "sections.showProducts", true) !== false;

                return (
                  <Box key={group.id}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      sx={{ mb: 1.15 }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{group.title}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)" }}>
                          {group.description}
                        </Typography>
                      </Box>

                      {commercialGroup ? (
                        <FormControlLabel
                          sx={{ m: 0, mr: { sm: 0.5 } }}
                          control={
                            <Switch
                              size="small"
                              disabled={!productsEnabled}
                              checked={commercialEnabled}
                              onChange={(event) => change("sections.showProducts", event.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 850 }}>
                              {commercialEnabled ? "Zona comercial activa" : "Zona comercial oculta"}
                            </Typography>
                          }
                        />
                      ) : null}
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2, minmax(0,1fr))" },
                        gap: 1.35,
                      }}
                    >
                      {definitions.map((definition) => {
                        const ownVisible = definition.visibilityPath
                          ? getAtPath(draft, definition.visibilityPath, definition.defaultVisible) !== false
                          : true;
                        const active = definition.requiresProducts
                          ? productsEnabled && commercialEnabled && ownVisible
                          : ownVisible;
                        const disabled = definition.requiresProducts && !productsEnabled;

                        return (
                          <SegmentChoiceCard
                            key={definition.id}
                            definition={definition}
                            active={active}
                            disabled={disabled}
                            onToggle={(nextVisible) =>
                              changeMany(buildSegmentVisibilityChanges(definition.id, nextVisible))
                            }
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          ) : null}

          {tab === 3 ? (
            <Grid container spacing={2}>
              <Grid item xs={12}><Field form={draft} path="business.name" label="Nombre del negocio" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.shortDescription" label="Descripción breve" onChange={change} multiline minRows={3} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.whatsapp" label="WhatsApp" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.phone" label="Teléfono" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.email" label="Correo electrónico" type="email" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.address" label="Dirección" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.city" label="Ciudad" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.province" label="Provincia" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.country" label="País" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.schedule" label="Horarios" onChange={change} multiline minRows={2} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.mapEmbedUrl" label="URL de mapa embebido" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.instagram" label="Instagram" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.facebook" label="Facebook" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.linkedin" label="LinkedIn" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.website" label="Sitio externo" onChange={change} /></Grid>
            </Grid>
          ) : null}

          {tab === 4 ? (
            <Stack spacing={2}>
              {mode !== "legacy" ? (
                <>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: "#19242F" }}>Identidad técnica y compartir</Typography>
                    <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                      Archivos globales que no pertenecen a una sección visual concreta.
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <EditableImage
                        value={getAtPath(draft, "theme.faviconUrl", "")}
                        label="Favicon"
                        helperText="Ícono cuadrado de la pestaña del navegador."
                        disabled={saving}
                        accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                        onUpload={(file, options) => upload("theme.faviconUrl", file, { sectionKey: "branding", assetKey: "favicon" }, options)}
                        onRemove={() => remove("theme.faviconUrl")}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <EditableImage
                        value={getAtPath(draft, "seo.imageUrl", "")}
                        label="Imagen para compartir"
                        helperText="Vista previa global para redes sociales y mensajería."
                        disabled={saving}
                        onUpload={(file, options) => upload("seo.imageUrl", file, { sectionKey: "seo", assetKey: "social" }, options)}
                        onRemove={() => remove("seo.imageUrl")}
                      />
                    </Grid>
                  </Grid>
                  <Divider />
                </>
              ) : null}
              <FormControl fullWidth disabled={busy}>
                <InputLabel>Estado público</InputLabel>
                <Select
                  label="Estado público"
                  value={selectedPublicStatus}
                  onChange={(event) => {
                    change("publicStatus", event.target.value);
                    setPendingPublicStatus("");
                  }}
                  inputProps={{ "aria-describedby": "public-status-help" }}
                >
                  <MenuItem value="draft">Borrador</MenuItem>
                  <MenuItem value="published">Publicado</MenuItem>
                  <MenuItem value="maintenance">Mantenimiento</MenuItem>
                </Select>
              </FormControl>
              <Typography id="public-status-help" variant="body2" sx={{ color: "text.secondary" }}>
                Estado actual del servidor: <strong>{publicStatusLabel(currentPublicStatus)}</strong>.
                La selección no modifica la web hasta que confirmes la transición.
              </Typography>
              <Field form={draft} path="seo.title" label="Título para Google y redes" onChange={change} />
              <Field form={draft} path="seo.description" label="Descripción para Google y redes" onChange={change} multiline minRows={4} />
              <Field form={draft} path="messages.maintenanceTitle" label="Título de mantenimiento" onChange={change} />
              <Field form={draft} path="messages.maintenanceDescription" label="Mensaje de mantenimiento" onChange={change} multiline minRows={3} />
              <Alert severity={selectedPublicStatus === "published" ? "success" : "warning"}>
                {selectedPublicStatus === "published"
                  ? "Publicado toma siempre el último borrador guardado y crea una revisión pública real."
                  : selectedPublicStatus === "maintenance"
                    ? "Mantenimiento conserva la última publicación y muestra solamente el aviso público."
                    : "Borrador oculta la web pública sin borrar publicaciones ni revisiones anteriores."}
              </Alert>
              {pendingPublicStatus ? (
                <Alert severity="info" role="alert">
                  <Typography sx={{ fontWeight: 900 }}>
                    Confirmar cambio a {publicStatusLabel(pendingPublicStatus)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {statusConfirmationMessage(pendingPublicStatus)}
                  </Typography>
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
        }}
      >
        {pendingPublicStatus ? (
          <>
            <Button
              onClick={() => setPendingPublicStatus("")}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 850, width: { xs: "100%", sm: "auto" } }}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={() => persist(pendingPublicStatus)}
              disabled={busy}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A", width: { xs: "100%", sm: "auto" } }}
            >
              {busy ? "Aplicando…" : `Confirmar ${publicStatusLabel(pendingPublicStatus)}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 850, width: { xs: "100%", sm: "auto" } }}>Cancelar</Button>
            <Button variant="contained" onClick={save} disabled={busy} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A", width: { xs: "100%", sm: "auto" } }}>
              {busy ? "Guardando…" : publicStatusChanged ? "Revisar transición" : mode === "sections" ? "Guardar secciones" : mode === "identity" ? "Guardar datos" : "Guardar preferencias"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
